import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { trackFilter, trackAction, trackDownload } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { BarChart3, Download, Filter, FileText, Loader2, FileSpreadsheet, Package } from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { toast } from 'sonner';
import { downloadFile } from '../lib/utils';

const COLORS = ['#00B2A9', '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

const ReportsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(null);
  const [generatingExcel, setGeneratingExcel] = useState(null);
  const [movementsByType, setMovementsByType] = useState(null);
  const [topResponsiblesReturns, setTopResponsiblesReturns] = useState(null);
  const [pendingEquipment, setPendingEquipment] = useState(null);
  
  // Equipment list for specific reports
  const [equipment, setEquipment] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState('all');
  const [includeSerialNumbers, setIncludeSerialNumbers] = useState(false);
  
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    reportType: 'all'
  });

  const loadReports = async () => {
    setLoading(true);
    try {
      const apiInstance = api();
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);

      const [typeRes, returnsRes, pendingRes, equipmentRes] = await Promise.all([
        apiInstance.get(`/reports/movements-by-type?${params}`),
        apiInstance.get('/reports/top-responsibles-returns'),
        apiInstance.get('/reports/pending-equipment'),
        apiInstance.get('/equipment')
      ]);

      setMovementsByType(typeRes.data);
      setTopResponsiblesReturns(returnsRes.data);
      setPendingEquipment(pendingRes.data);
      setEquipment(equipmentRes.data || []);
    } catch (error) {
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (reportType) => {
    setGeneratingPDF(reportType);
    try {
      const apiInstance = api();
      const params = new URLSearchParams();
      
      // Add options for inventory report
      if (reportType === 'inventory') {
        if (includeSerialNumbers) params.append('include_serials', 'true');
        if (selectedEquipment !== 'all') params.append('equipment_id', selectedEquipment);
      }
      
      const url = params.toString() 
        ? `/reports/pdf/${reportType}?${params}` 
        : `/reports/pdf/${reportType}`;
      
      const response = await apiInstance.get(url, {
        responseType: 'blob'
      });
      
      const reportNames = {
        inventory: 'Inventario',
        movements: 'Movimientos',
        responsibles: 'Responsables',
        'serial-numbers': 'NumerosDeSerie'
      };
      
      downloadFile(response.data, `Reporte_${reportNames[reportType]}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Reporte PDF descargado');
    } catch (error) {
      toast.error('Error al generar PDF');
    } finally {
      setGeneratingPDF(null);
    }
  };

  const handleDownloadExcel = async (reportType) => {
    setGeneratingExcel(reportType);
    try {
      const apiInstance = api();
      const params = new URLSearchParams();
      
      if (reportType === 'inventory') {
        if (includeSerialNumbers) params.append('include_serials', 'true');
        if (selectedEquipment !== 'all') params.append('equipment_id', selectedEquipment);
      }
      
      const url = params.toString() 
        ? `/reports/excel/${reportType}?${params}` 
        : `/reports/excel/${reportType}`;
      
      const response = await apiInstance.get(url, {
        responseType: 'blob'
      });
      
      const reportNames = {
        inventory: 'Inventario',
        movements: 'Movimientos',
        responsibles: 'Responsables',
        'serial-numbers': 'NumerosDeSerie'
      };
      
      downloadFile(response.data, `Reporte_${reportNames[reportType]}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Reporte Excel descargado');
    } catch (error) {
      toast.error('Error al generar Excel');
    } finally {
      setGeneratingExcel(null);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const movementTypeData = movementsByType ?
    movementsByType.labels.map((label, idx) => ({
      name: label,
      value: movementsByType.data[idx]
    })) : [];

  const returnsData = topResponsiblesReturns ?
    topResponsiblesReturns.labels.map((label, idx) => ({
      name: label,
      buenos: topResponsiblesReturns.datasets[0]?.data[idx] || 0,
      malos: topResponsiblesReturns.datasets[1]?.data[idx] || 0
    })) : [];

  const pendingData = pendingEquipment ?
    pendingEquipment.labels.map((label, idx) => ({
      name: label,
      pendiente: pendingEquipment.data[idx]
    })) : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reportes</h1>
          <p className="text-muted-foreground mt-1">
            Análisis y estadísticas del inventario
          </p>
        </div>
      </div>

      {/* PDF Reports Section */}
      <Card className="bg-gradient-to-r from-[#00B2A9]/10 to-transparent border-primary/30">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Generar Reportes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Report Options */}
          <div className="p-4 bg-muted/30 rounded-lg space-y-4">
            <p className="text-sm font-medium flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Opciones del Reporte de Inventario
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Equipo Específico</Label>
                <Select value={selectedEquipment} onValueChange={setSelectedEquipment}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos los equipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los equipos</SelectItem>
                    {equipment.map(eq => (
                      <SelectItem key={eq.id} value={eq.id}>{eq.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Checkbox 
                  id="include-serials" 
                  checked={includeSerialNumbers}
                  onCheckedChange={setIncludeSerialNumbers}
                />
                <Label htmlFor="include-serials" className="text-sm">
                  Incluir números de serie
                </Label>
              </div>
            </div>
          </div>

          {/* PDF Buttons */}
          <div>
            <p className="text-sm font-medium mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-red-500" />
              Exportar a PDF
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button 
                onClick={() => handleDownloadPDF('inventory')} 
                disabled={generatingPDF === 'inventory'}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:border-primary hover:bg-primary/5"
                data-testid="pdf-inventory-btn"
              >
                {generatingPDF === 'inventory' ? (
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                ) : (
                  <Download className="h-6 w-6 text-primary" />
                )}
                <span className="font-medium">Reporte de Inventario</span>
                <span className="text-xs text-muted-foreground">
                  {selectedEquipment !== 'all' ? 'Equipo filtrado' : 'Stock actual por ubicación'}
                </span>
              </Button>
              
              <Button 
                onClick={() => handleDownloadPDF('serial-numbers')} 
                disabled={generatingPDF === 'serial-numbers'}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:border-primary hover:bg-primary/5"
                data-testid="pdf-serial-numbers-btn"
              >
                {generatingPDF === 'serial-numbers' ? (
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
                ) : (
                  <Download className="h-6 w-6 text-emerald-500" />
                )}
                <span className="font-medium">Números de Serie</span>
                <span className="text-xs text-muted-foreground">Listado por ubicación</span>
              </Button>
              
              <Button 
                onClick={() => handleDownloadPDF('movements')} 
                disabled={generatingPDF === 'movements'}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:border-primary hover:bg-primary/5"
                data-testid="pdf-movements-btn"
              >
                {generatingPDF === 'movements' ? (
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                ) : (
                  <Download className="h-6 w-6 text-blue-500" />
                )}
                <span className="font-medium">Reporte de Movimientos</span>
                <span className="text-xs text-muted-foreground">Historial de salidas y retornos</span>
              </Button>
              
              <Button 
                onClick={() => handleDownloadPDF('responsibles')} 
                disabled={generatingPDF === 'responsibles'}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:border-primary hover:bg-primary/5"
                data-testid="pdf-responsibles-btn"
              >
                {generatingPDF === 'responsibles' ? (
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                ) : (
                  <Download className="h-6 w-6 text-amber-500" />
                )}
                <span className="font-medium">Equipos por Responsable</span>
                <span className="text-xs text-muted-foreground">Pendientes de devolución</span>
              </Button>
            </div>
          </div>

          {/* Excel Buttons */}
          <div>
            <p className="text-sm font-medium mb-3 flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
              Exportar a Excel
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button 
                onClick={() => handleDownloadExcel('inventory')} 
                disabled={generatingExcel === 'inventory'}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:border-emerald-500 hover:bg-emerald-500/5 border-emerald-500/30"
                data-testid="excel-inventory-btn"
              >
                {generatingExcel === 'inventory' ? (
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                ) : (
                  <FileSpreadsheet className="h-6 w-6 text-emerald-600" />
                )}
                <span className="font-medium">Inventario Excel</span>
                <span className="text-xs text-muted-foreground">
                  {selectedEquipment !== 'all' ? 'Equipo filtrado' : 'Datos completos'}
                </span>
              </Button>
              
              <Button 
                onClick={() => handleDownloadExcel('serial-numbers')} 
                disabled={generatingExcel === 'serial-numbers'}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:border-emerald-500 hover:bg-emerald-500/5 border-emerald-500/30"
                data-testid="excel-serial-numbers-btn"
              >
                {generatingExcel === 'serial-numbers' ? (
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                ) : (
                  <FileSpreadsheet className="h-6 w-6 text-emerald-600" />
                )}
                <span className="font-medium">Series Excel</span>
                <span className="text-xs text-muted-foreground">Todos los números</span>
              </Button>
              
              <Button 
                onClick={() => handleDownloadExcel('movements')} 
                disabled={generatingExcel === 'movements'}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:border-emerald-500 hover:bg-emerald-500/5 border-emerald-500/30"
                data-testid="excel-movements-btn"
              >
                {generatingExcel === 'movements' ? (
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                ) : (
                  <FileSpreadsheet className="h-6 w-6 text-emerald-600" />
                )}
                <span className="font-medium">Movimientos Excel</span>
                <span className="text-xs text-muted-foreground">Historial completo</span>
              </Button>
              
              <Button 
                onClick={() => handleDownloadExcel('responsibles')} 
                disabled={generatingExcel === 'responsibles'}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:border-emerald-500 hover:bg-emerald-500/5 border-emerald-500/30"
                data-testid="excel-responsibles-btn"
              >
                {generatingExcel === 'responsibles' ? (
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                ) : (
                  <FileSpreadsheet className="h-6 w-6 text-emerald-600" />
                )}
                <span className="font-medium">Responsables Excel</span>
                <span className="text-xs text-muted-foreground">Equipos pendientes</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="card-hover">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5 text-primary" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Fecha Inicio</Label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                data-testid="start-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha Fin</Label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                data-testid="end-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo de Reporte</Label>
              <Select
                value={filters.reportType}
                onValueChange={(value) => setFilters({ ...filters, reportType: value })}
              >
                <SelectTrigger data-testid="report-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="movements">Movimientos</SelectItem>
                  <SelectItem value="returns">Retornos</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={loadReports} className="btn-primary w-full" data-testid="apply-filters-btn">
                Aplicar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Movements by Type */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="text-lg">Movimientos por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : movementTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={movementTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {movementTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No hay datos disponibles
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Responsibles Returns */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="text-lg">Top 10 Responsables - Retornos</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : returnsData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={returnsData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis dataKey="name" type="category" width={100} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="buenos" stackId="a" fill="#10B981" name="Buenos" />
                  <Bar dataKey="malos" stackId="a" fill="#EF4444" name="Malos" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No hay datos disponibles
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pending Equipment */}
      <Card className="chart-container">
        <CardHeader>
          <CardTitle className="text-lg">Top 10 Responsables - Equipos Pendientes</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-80 w-full" />
          ) : pendingData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={pendingData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} angle={-45} textAnchor="end" height={80} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="pendiente" fill="#F59E0B" name="Equipos Pendientes" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-80 flex items-center justify-center text-muted-foreground">
              No hay equipos pendientes de devolución
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
